import { mutate } from "swr";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect } from "react";

import { ITemp } from "../users-table";
import usePutApi from "../../../../hooks/use-put-api";
import parseNumber from "../../../../utils/parse-number";
import endpoints, { IMember } from "../../../../api/endpoints";
import { swrKeyMatchesAllpanelDownlineList } from "../../../../api/swr-revalidate";
import DepositSchema, { IDepositSchema } from "./schema/deposit.schema";
import { showErrorToast, showSuccessToast } from "src/utils/toast";
import { useSyncGlobalPageLoader } from "../../../../contexts/global-page-loader-context";

interface Props {
  openModal: boolean;
  toggleModal: () => void;
  rowData: ITemp;
  parentData: IMember | undefined;
  parentUserId: string;
}

const DepositModal = ({ openModal, toggleModal, rowData, parentData, parentUserId }: Props) => {
  const [upBalance, setUpBalance] = useState(parentData?.balance ?? 0);
  const [downBalance, setDownBalance] = useState(0);
  const [remainingUpBalance, setRemainingUpBalance] = useState(0);

  const { values, errors, handleChange, handleSubmit, handleBlur, resetForm, setFieldValue } = useFormik({
    initialValues: {
      remark: "",
      amount: "",
      transaction_password: "",
      member_operation_type: "deposit",
    },
    onSubmit: (formValues) => {
      handleDepositSubmit({ ...formValues, amount: +values.amount });
    },
    validationSchema: DepositSchema,
  });

  const { putData, loading: putLoading } = usePutApi(endpoints.member.operation(rowData?.id), [
    endpoints.member.getMember(parentUserId),
    swrKeyMatchesAllpanelDownlineList(parentUserId),
  ]);

  useSyncGlobalPageLoader(putLoading);

  const handleDepositSubmit = async (submitValues: IDepositSchema) => {
    try {
      await putData({
        ...submitValues,
        admin_id: parentUserId,
        amount: parseNumber({ type: "float", value: values.amount }),
      });
      showSuccessToast("Successfull Balance Transfer");
      toggleModal();
      resetForm();
    } catch (submitError: any) {
      const errorMessage = submitError?.data?.status_message;
      showErrorToast(errorMessage || "Someting Went Wrong");
      toggleModal();
      resetForm();
    }
  };

  useEffect(() => {
    setUpBalance(+(parentData?.balance ?? 0));
  }, [parentData?.balance]);

  useEffect(() => {
    if (rowData && rowData?.available_balance) {
      setDownBalance(rowData?.available_balance);
    }
  }, [rowData]);

  useEffect(() => {
    setRemainingUpBalance(upBalance - +values.amount);
  }, [values.amount, upBalance, downBalance]);

  useEffect(() => {
    mutate(endpoints.member.getMember(parentUserId));
  }, [parentUserId]);

  if (!openModal) return null;

  return (
    <>
    {/* style={{ position: "absolute", zIndex: 1040 }} */}
      <div
        role="dialog"
        className="modal fade show"
        aria-modal="true"
        style={{ display: "block" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            toggleModal();
          }
        }}
      >
        <div className="modal-dialog modal-md modal-dialog-scrollable">
          <span />
          <div className="modal-content" style={{ marginTop: "60px" }}>
            <header className="modal-header">
              <h4 className="modal-title">Deposit</h4>
              <button type="button" data-dismiss="modal" className="close text-white" onClick={toggleModal}>
                &times;
              </button>
            </header>
            <div className="modal-body">
              <form method="post" onSubmit={handleSubmit}>
                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userDipositeloginusramount">
                    {parentData?.user_name}
                  </label>
                  <div className="col-8">
                    <div className="row">
                      <div className="col-6">
                        <input
                          id="userDipositeloginusramount"
                          type="text"
                          readOnly
                          value={parentData?.balance ?? 0}
                          name="userDipositeloginusramount"
                          className="form-control txt-right"
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          readOnly
                          value={+values.amount > 0 ? remainingUpBalance : (parentData?.balance ?? 0)}
                          name="userDipositeloginusrNamount"
                          className="form-control txt-right"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userDipositeusrnameamount">
                    {rowData.username}
                  </label>
                  <div className="col-8">
                    <div className="row">
                      <div className="col-6">
                        <input
                          id="userDipositeusrnameamount"
                          type="text"
                          readOnly
                          value={rowData?.available_balance ?? 0}
                          name="userDipositeusrnameamount"
                          className="form-control txt-right"
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          readOnly
                          value={+values.amount > 0 ? +(rowData?.available_balance ?? 0) + +values?.amount : "0"}
                          name="userDipositeusrnameNamount"
                          className="form-control txt-right"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userDipositeamount">
                    Amount
                  </label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <input
                      id="userDipositeamount"
                      placeholder="Amount"
                      type="number"
                      name="amount"
                      className={`form-control txt-right ${errors.amount ? "is-invalid" : ""}`}
                      value={values.amount}
                      onClick={() => {
                        if (+values.amount === 0) {
                          setFieldValue("amount", "");
                        }
                      }}
                      onChange={handleChange}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (values.amount === "") {
                          setFieldValue("amount", "");
                        }
                      }}
                    />
                    {/* {errors.amount && <span className="text-danger small">{errors.amount}</span>} */}
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userDipositeremark">
                    Remark
                  </label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <textarea
                      id="userDipositeremark"
                      placeholder="Remark"
                      name="remark"
                      className={`form-control ${errors.remark ? "is-invalid" : ""}`}
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {/* {errors.remark && <span className="text-danger small">{errors.remark}</span>} */}
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userDipositepassword">
                    Transaction Password
                  </label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <input
                      id="userDipositepassword"
                      name="transaction_password"
                      type="password"
                      className={`form-control ${errors.transaction_password ? "is-invalid" : ""}`}
                      value={values.transaction_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {/* {errors.transaction_password && (
                      <span className="text-danger small">{errors.transaction_password}</span>
                    )} */}
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-12 text-right">
                    <button type="button" className="btn btn-back" onClick={toggleModal}>
                      <i className="fas fa-undo mr-1" />
                      Back
                    </button>{" "}
                    <button type="submit" className="btn btn-primary">
                      Submit
                      <i className="fas fa-sign-in-alt ml-1" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <span />
        </div>
      </div>
      <div className="modal-backdrop" role="button" tabIndex={0} aria-label="Close deposit modal" onClick={toggleModal} />
    </>
  );
};

export default DepositModal;
